import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Колокольчик уведомлений. Пока единственный тип события — самостоятельная
 * запись кандидата на стажировку: именно его руководитель обучения ждёт,
 * чтобы подтвердить дату.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: api.notifications,
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: api.markNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = notifications.data ?? [];
  const unread = items.filter((item) => item.readAt === null).length;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen(!open);
          if (!open && unread > 0) markRead.mutate();
        }}
        className="relative rounded-[9px] border border-border bg-surface-2 px-3 py-2 text-[14px] hover:bg-border"
        title="Уведомления"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-bad px-1 text-[10px] font-extrabold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-40 mt-2 max-h-[400px] w-[320px] overflow-y-auto rounded-card border border-border bg-surface p-2 shadow-card">
            <p className="px-2 py-1.5 text-[11px] font-extrabold tracking-wider text-ink-faint uppercase">
              Уведомления
            </p>

            {items.length === 0 && (
              <p className="px-2 py-4 text-center text-[12.5px] text-ink-faint">
                Пока ничего нового
              </p>
            )}

            {items.map((item) => (
              <div key={item.id} className="rounded-[8px] px-2 py-2 hover:bg-surface-2">
                <p className="m-0 text-[13px] leading-snug">{describe(item)}</p>
                <p className="m-0 mt-0.5 text-[11px] text-ink-faint">
                  {new Date(item.createdAt).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function describe(item: { type: string; payload: Record<string, unknown> }): string {
  if (item.type === 'internship_requested') {
    const name = String(item.payload.name ?? 'Кандидат');
    const date = String(item.payload.date ?? '');
    const human = date ? new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU') : '';
    return `${name} записался на стажировку${human ? ` на ${human}` : ''}`;
  }
  if (item.type === 'stuck_candidates') {
    const count = Number(item.payload.count ?? 0);
    const days = Number(item.payload.days ?? 0);
    return `${count} ${plural(count, 'кандидат', 'кандидата', 'кандидатов')} не двигаются по обучению дольше ${days} ${plural(days, 'дня', 'дней', 'дней')}`;
  }

  return 'Новое событие';
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;

  const mod10 = count % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
